// Phase 43 — pure per-client MATTER computation, shared by the Clients filter
// bar (item 1), most-recent status/date surfacing (item 2) and the client-record
// enrichment (item 3). Everything here is COMPUTED at render from already-loaded
// arrays (ca / cc / inv) — NOTHING is denormalized/copied onto the Client row
// (D14). PURE: no React, no DOM, no network.

// 141 §2.1 — the lifecycle date slots, from the module that owns the
// vocabulary. See the note above `linkStatusDate` for what this replaced and
// why it could not stay a local copy.
import { linkStatusDateCol } from "./collectiveSegments.mjs";

// case_status code → the companion date column that records when the case
// ENTERED that status (item 2). Codes are the case_status taxonomy defaults
// (active/pending/closed/archived — src/utils/taxonomyMap.js DEFAULT_CASE_STATUSES);
// the columns are auto-set client-side on the status flip and persisted server
// side (Phase 30 D-1 #3). Any unmapped status falls back to updatedAt.
const STATUS_DATE_COL = {
  active: "openDate",
  pending: "pendingDate",
  closed: "closedDate",
  archived: "archivedDate",
};

// The date the case's CURRENT status was last set. Companion column for the
// status value, else `statusDate` — 209 §23 [RULED 20]: a status date moves only
// when its status moves, so `updatedAt` (which every write of the row moves: a
// title, a note, a linked item, a member) no longer dates a status. It is read
// only for a row that has no `statusDate` YET — one the deploy's fill has not
// reached, or a row cached before it (the boot caches) — so no date shown moves
// in between. Returns a Date or null.
export function caseStatusDate(c) {
  if (!c) return null;
  const col = STATUS_DATE_COL[c.status];
  const raw = (col && c[col]) || c.statusDate || c.updatedAt || c.createdAt || null;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

// Regular matters (Case) a client is linked to, via the flattened clIds array.
export function clientRegularCases(clientId, cases) {
  return (cases || []).filter((c) => (c.clIds || []).includes(clientId));
}

// Collective matters a client is linked to, via clientLinks[].clId.
export function clientCollectiveCases(clientId, ccs) {
  return (ccs || []).filter(
    (c2) => (c2.clientLinks || []).some((lk) => lk.clId === clientId),
  );
}

// P17.1c (RULINGS 2026-07-18) — the credit-aware per-invoice "amount due",
// server-authoritative. Prefer the served figure (shapes.js amountDue =
// totalAmount − credits − payments); fall back to the SAME formula locally for
// boot-cache rows that predate the served field, so the figure is never
// credit-blind. VAT-incl; totalToPay (= totalAmount) already nets commercial +
// retainer deductions.
export function invoiceAmountDue(i) {
  if (!i) return 0;
  if (i.amountDue != null) return Number(i.amountDue) || 0;
  const credits = Array.isArray(i.credits)
    ? i.credits.reduce((s, c) => s + Number(c.amount || 0), 0) : 0;
  const paid = (i.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
  return Number(i.totalToPay || 0) - credits - paid;
}

// 191 E.2g — "ANYTHING OWED" IS HALF A CENT, NAMED ONCE. Money in this tree is
// unrounded IEEE-754 doubles (RULINGS 2026-07-09), so an amount due can carry a
// sub-cent residue: PF-2026/008 (recordings/156-payment-rows/README.md) totals
// 107.9925, was paid 107.99, is stored `paid`, and its amountDue is 0.0025 — a
// figure that formats to "0,00 €" and read as outstanding. The house already
// had the rule in three spellings — ClientCasesCard's `due > 0.005` (twice),
// retainerRounds.js's `amountDue > 0.005 // 0.5-cent tolerance` — and this is
// the one name they, InvoiceTable's DÛ cell and the strip's DÛ colour read.
// A tolerance on the QUESTION, never on the figure: the served amount is what
// it is; what changes is whether a residue below half a cent is called owed.
export const OWED_TOLERANCE = 0.005;
export function isOwed(amount) {
  return Number(amount || 0) > OWED_TOLERANCE;
}

// 191 E.2f — OVERDUE, ONE DEFINITION. Phase 35 Stage-3's predicate — unpaid
// or partially paid AND past the due date, no grace — lived twice, character
// for character: InvoiceTable.tsx (the row's DÛ colour and, until 191, its
// badge) and InvoiceView.tsx (the status picker's "Overdue" pill and its
// count). One export now, beside the amount it qualifies. THE ONE DIFFERENCE
// THE TWO COPIES HAD is kept as the parameter: InvoiceView took its `todayStr`
// ONCE per render and hands it in; InvoiceTable took it per call and still
// does (the default). A row and the pill that counts it therefore read the
// same clock within a render either way.
export function isOverduePF(i, today = new Date().toISOString().slice(0, 10)) {
  return !!(
    i &&
    ["unpaid", "partially_paid"].includes(i.paymentStatus) &&
    i.dueDate &&
    String(i.dueDate).slice(0, 10) < today
  );
}

// Credit-aware pro-forma "amount due" for ONE matter: Σ invoiceAmountDue over
// this matter's non-cancelled pro-formas — the same credit-aware figure
// ReportsView "outstanding" and the pro-forma "Due" card now show. `credits`
// (paid retainer) are subtracted (they were NOT until P17.1c — a live
// overstatement once a matter carries a credited pro forma). matterKey:
// "caseId" for a Case, "ccId" for a CollectiveCase. Labelled pro-forma, never
// "debt"/"owed" (D20) — that framing lives at the call sites.
// 169 R7 — AGGREGATES PER CURRENCY: the sum runs over the documents in the
// FIRM's currency (`firm`, EUR by default — every row that names no currency
// is the firm's), exactly as it ran over every document while every document
// was the firm's; `matterAmountDueForeign` answers the other currencies, one
// figure per code, or null. No sum adds two currencies.
export function matterAmountDue(matterId, matterKey, invoices, firm = "EUR") {
  let due = 0;
  for (const i of invoices || []) {
    if (i.type !== "proforma") continue;
    if (i.paymentStatus === "cancelled") continue;
    if (i[matterKey] !== matterId) continue;
    if ((i.currency || firm) !== firm) continue;
    due += invoiceAmountDue(i);
  }
  return due;
}
export function matterAmountDueForeign(matterId, matterKey, invoices, firm = "EUR") {
  const out = {};
  for (const i of invoices || []) {
    if (i.type !== "proforma") continue;
    if (i.paymentStatus === "cancelled") continue;
    if (i[matterKey] !== matterId) continue;
    const code = i.currency || firm;
    if (code === firm) continue;
    out[code] = (out[code] || 0) + invoiceAmountDue(i);
  }
  return Object.keys(out).length ? out : null;
}
// Σ of per-code maps, in place: `mergeForeign(acc, matterAmountDueForeign(...))`.
export function mergeForeign(acc, more) {
  if (!more) return acc;
  const out = acc || {};
  for (const [code, v] of Object.entries(more)) out[code] = (out[code] || 0) + v;
  return out;
}

// Phase 43 CORRECTION (item 3) — membership lifecycle. A member's status
// carries a companion date slot, so a collective-only client is no longer
// statusless for most-recent ranking.
//
// 141 §2.1 — THE THREE-SLOT TWIN THAT STOOD HERE IS GONE. It knew Active /
// Suspended / Terminated and was written before Prospect and Archived existed,
// so a member moved to either of the two new values by §1's picker read as
// DATELESS here — and a dateless link is skipped by `consider()` below, which
// is the function that decides which of a client's matters is the most recent
// one. The map now has one owner beside the vocabulary it belongs to, and the
// same map answers for a regular case's link (§2.1 gave `CaseClient` the same
// five columns, by the same names, for exactly this reason). Imported at the
// head of the file, where this module's only import belongs.
export function collectiveLinkFor(clientId, cc) {
  return (
    (cc && (cc.clientLinks || []).find((lk) => lk.clId === clientId)) || null
  );
}
// 141 §2.3 — WAS `collectiveLinkStatusDate`, and the name stopped being true.
// §2.1 gave `CaseClient` the same five date columns under the same names, so
// this answers for a regular case's link exactly as it does for a collective
// member's. Widened rather than duplicated (rule 4).
export function linkStatusDate(lk) {
  if (!lk) return null;
  const col = linkStatusDateCol(lk.status);
  const raw = col && lk[col];
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

// 141 §2.3 — ONE LINK, EITHER KIND OF MATTER. A collective member's link is an
// element of the matter's own `clientLinks` JSON; a regular case's is a row of
// the CaseClient junction, served as `clLinks` keyed by client id (§2.1). The
// two are told apart by which key the row carries, never by a flag the caller
// passes — a caller that gets the flag wrong reads the wrong shape silently.
export function matterLinkFor(clientId, matter) {
  if (!matter) return null;
  if (Array.isArray(matter.clientLinks)) return collectiveLinkFor(clientId, matter);
  const m = matter.clLinks;
  if (!m || typeof m !== "object") return null;
  return m[clientId] || m[String(clientId)] || null;
}

// 141 §2.3 — THE RULE, ONCE: what a client's standing in a matter IS.
//
// §2.3 rules it for the regular arm — "the client's own link status if set,
// else the case's status" — and it is applied to both kinds, because §0-bis's
// whole point is that the two answer the same question the same way. A
// collective member without a status (measured 2026-08-10: 40 of 303 elements
// carry no `status` key at all) therefore stops showing nothing and shows the
// matter's own, exactly as a statusless regular link does.
//
// COMPUTED, NEVER DENORMALIZED — the header's own promise, kept: both sides of
// the choice are read off rows the caller already holds.
//
// Returns { status, date, link, fromLink } — `fromLink` because a caller that
// renders the two differently (the client record's matter card does: the link
// vocabulary wears its own pill, the matter's status wears `.status`) must not
// have to re-derive which branch it got.
export function clientMatterStanding(clientId, matter) {
  const link = matterLinkFor(clientId, matter);
  const own = (link && link.status) || null;
  if (own) return { status: own, date: linkStatusDate(link), link, fromLink: true };
  return {
    status: (matter && matter.status) || null,
    date: caseStatusDate(matter),
    link,
    fromLink: false,
  };
}

// Per-client rollup for the filter bar (over already-loaded ca/cc/inv). Returns
// the derived axes item 1 filters on + item 2's most-recent status/date.
//   { hasCaseLink, mostRecentStatus, mostRecentStatusDate, mostRecentStanding,
//     mostRecentMatter, totalDue, hasDue, titles, matterStatuses }
// "Most recent" = the latest status-change across the client's regular cases
// (companion date) AND collective memberships (lifecycle date, item 3).
export function clientMatterSummary(client, cases, ccs, invoices) {
  const reg = clientRegularCases(client.id, cases);
  const col = clientCollectiveCases(client.id, ccs);

  let mostRecentStatus = null;
  let mostRecentDate = null;
  // 168B §1c — WHICH standing won, and on WHICH matter. `mostRecentStatus` is
  // a bare string, and a bare string cannot be drawn: the client record's
  // matter card renders a status the client OWNS in the link vocabulary's
  // colours and a status INHERITED from the matter muted (141 §2.3 / RULED
  // 2026-08-27), and it tells them apart by `fromLink`. The Clients list shows
  // the same value and must wear the same pill, so the two facts the renderer
  // needs are carried out rather than re-derived from a string that has lost
  // them. The matter comes too, for its own prospect tag (140 §1.2).
  let mostRecentStanding = null;
  let mostRecentMatter = null;
  const consider = (status, d, standing, matter) => {
    if (d && (!mostRecentDate || d > mostRecentDate)) {
      mostRecentDate = d;
      mostRecentStatus = status || null;
      mostRecentStanding = standing || null;
      mostRecentMatter = matter || null;
    }
  };
  // 141 §2.3 — both loops now ask `clientMatterStanding`, which is the ruled
  // rule. The regular arm used to read `c.status` alone and so ranked a client
  // by the MATTER's status even where the client had been given one of their
  // own; the collective arm's own two lines were that rule half-written.
  for (const c of [...reg, ...col]) {
    const st = clientMatterStanding(client.id, c);
    consider(st.status, st.date, st, c);
  }

  let totalDue = 0;
  let totalDueForeign = null;
  for (const c of reg) {
    totalDue += matterAmountDue(c.id, "caseId", invoices);
    totalDueForeign = mergeForeign(totalDueForeign, matterAmountDueForeign(c.id, "caseId", invoices));
  }
  for (const c of col) {
    totalDue += matterAmountDue(c.id, "ccId", invoices);
    totalDueForeign = mergeForeign(totalDueForeign, matterAmountDueForeign(c.id, "ccId", invoices));
  }
  // 169 R7 — a client is "due" when ANY currency is due.
  const foreignDue = totalDueForeign ? Object.values(totalDueForeign).some((v) => v > 0.005) : false;

  const titles = [
    ...reg.map((c) => c.title || c.ref || ""),
    ...col.map((c) => c.title || c.ref || ""),
  ].filter(Boolean);

  /* 168B §2b — THE MATTERS' OWN STATUSES, distinct, in first-seen order.
     `mostRecentStatus` answers "what is this client's STANDING", and since
     141 §2.3 that may be a LINK value (Prospect/Active/…). The Clients
     filter bar's Case-status axis offers `case_status` CODES
     (active/pending/…), so comparing the two could never match for a client
     whose standing came from a link — the filter silently lost them. The two
     questions are now asked of two fields: Case status of these, client
     status of `mostRecentStatus`. */
  const matterStatuses = [
    ...new Set([...reg, ...col].map((c) => c.status).filter(Boolean)),
  ];

  return {
    hasCaseLink: reg.length > 0 || col.length > 0,
    mostRecentStatus,
    mostRecentStatusDate: mostRecentDate,
    mostRecentStanding,
    mostRecentMatter,
    totalDue,
    totalDueForeign,
    hasDue: totalDue > 0.005 || foreignDue,
    titles,
    matterStatuses,
  };
}
